import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

import { AssinadorService } from './assinador.service';
import { SessionService } from './session.service';
import { WebPkiService } from './web-pki.service';
import { SelecionarCertificadoDialogComponent } from '../../features/anexar-documentos/selecionar-certificado-dialog.component';

export interface DocumentoAssinavel {
  nome?: string;
  nomeArquivo?: string;
  descricao?: string;
  hash?: string;
  assinado?: boolean;
  signing?: boolean;
}

export interface ResumoAssinatura {
  sucesso: string[];
  falha: { nome: string; motivo: string }[];
}

export interface ProgressoAssinatura {
  atual: number;
  total: number;
}

/**
 * Motivo legível de uma falha. O militar-back responde {message} (403 de acesso),
 * {mensagem, codigo} (422 de regra de negócio e 500) ou {detail} (ResponseStatusException, ex.: 409
 * do excluir); com responseType 'text' o corpo chega como texto JSON. Os erros do Web PKI e os locais
 * trazem só "message". Vale o primeiro candidato não vazio: message, mensagem, detail, o corpo em
 * texto sem HTML e, por último, a mensagem do próprio erro.
 */
export function motivoDoErro(err: any): string {
  let corpo: any = err?.error;
  let textoDoCorpo: string | undefined;
  if (typeof corpo === 'string') {
    let json: any;
    try {
      json = JSON.parse(corpo);
    } catch {
      json = undefined;
    }
    if (json && typeof json === 'object') {
      corpo = json;
    } else {
      textoDoCorpo = typeof json === 'string' ? json : semHtml(corpo);
      corpo = undefined;
    }
  }
  const candidatos = [corpo?.message, corpo?.mensagem, corpo?.detail, textoDoCorpo, err?.message];
  for (const candidato of candidatos) {
    if (typeof candidato === 'string' && candidato.trim()) return candidato.trim();
  }
  return `HTTP ${err?.status ?? '???'}`;
}

/** Texto de uma página de erro (ex.: proxy) sem cabeçalho, scripts e tags. */
function semHtml(texto: string): string {
  return texto
    .replace(/<(head|script|style)\b[\s\S]*?<\/\1>/gi, ' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Aviso ao fim da assinatura; com falhas, agrupa os documentos por motivo:
 * "<n> assinado(s), <m> falharam: Documento já assinado (A, B) | <outro motivo> (C)".
 */
export function mensagemResumoAssinatura(resumo: ResumoAssinatura): string {
  if (resumo.falha.length === 0) {
    return `${resumo.sucesso.length} documento(s) assinado(s) com sucesso.`;
  }
  const nomesPorMotivo = new Map<string, string[]>();
  for (const falha of resumo.falha) {
    nomesPorMotivo.set(falha.motivo, [...(nomesPorMotivo.get(falha.motivo) ?? []), falha.nome]);
  }
  const grupos = Array.from(nomesPorMotivo, ([motivo, nomes]) => `${motivo} (${nomes.join(', ')})`);
  return `${resumo.sucesso.length} assinado(s), ${resumo.falha.length} falharam: ${grupos.join(' | ')}`;
}

@Injectable({ providedIn: 'root' })
export class AssinaturaLoteService {
  constructor(
    private webPkiService: WebPkiService,
    private assinadorService: AssinadorService,
    private sessionService: SessionService,
    private dialog: MatDialog
  ) {}

  public lerGuardiao(): any {
    const g = this.sessionService.guardiao;
    if (!g) return null;
    if (typeof g === 'string') {
      try { return JSON.parse(g); } catch { return null; }
    }
    return g;
  }

  public async assinarLote(
    pendentes: DocumentoAssinavel[],
    onProgress?: (p: ProgressoAssinatura) => void
  ): Promise<ResumoAssinatura | null> {
    const guardiao: any = this.lerGuardiao();
    const nomeUsuario: string =
      guardiao?.nome ??
      guardiao?.nomeUsuario ??
      guardiao?.usuario?.nome ??
      'GESTOR';

    const cert = await this.selecionarCertificado();
    if (!cert) return null;

    const resumo: ResumoAssinatura = { sucesso: [], falha: [] };
    let atual = 0;

    for (const doc of pendentes) {
      atual++;
      onProgress?.({ atual, total: pendentes.length });
      doc.signing = true;
      const nome = doc.nomeArquivo ?? doc.nome ?? doc.descricao ?? 'Documento';
      try {
        await this.assinarDocumento(doc, cert.thumbprint, cert.certContent, nomeUsuario, guardiao);
        doc.signing = false;
        doc.assinado = true;
        resumo.sucesso.push(nome);
      } catch (err: any) {
        doc.signing = false;
        resumo.falha.push({ nome, motivo: motivoDoErro(err) });
      }
    }

    return resumo;
  }

  public assinarDocumento(
    doc: DocumentoAssinavel,
    thumbprint: string,
    certContent: string,
    nomeUsuario: string,
    guardiao: any
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!doc.hash) {
        reject({ message: 'Documento sem hash original.' });
        return;
      }
      this.assinadorService
        .start({ hashDocumento: doc.hash, certContent })
        .subscribe({
          next: (startRes) => {
            this.webPkiService.assinarHash(thumbprint, startRes.toSignHash).subscribe({
              next: (signature) => {
                this.assinadorService
                  .complete({
                    fileToSign: startRes.fileName,
                    signature,
                    transferFileId: startRes.transferFileId,
                    hashDocumento: doc.hash!,
                    nomeUsuario,
                    cpfUsuario: guardiao?.cpf ?? guardiao?.CPF ?? undefined,
                    perfilUsuario: guardiao?.grupo?.nome ?? 'GESTOR',
                  })
                  .subscribe({
                    next: (res) => { (doc as any).hashAssinado = res?.hashAssinado; resolve(); },
                    error: (err) => reject(err),
                  });
              },
              error: (err) => reject(err),
            });
          },
          error: (err) => reject(err),
        });
    });
  }

  public selecionarCertificado(): Promise<{ thumbprint: string; certContent: string } | null> {
    return new Promise((resolve, reject) => {
      this.webPkiService.init().subscribe({
        next: () => {
          this.webPkiService.listarCertificados().subscribe({
            next: (certs) => {
              const ref = this.dialog.open(SelecionarCertificadoDialogComponent, {
                data: { certificados: certs },
                autoFocus: false,
              });
              ref.afterClosed().subscribe((thumbprint: string | null | undefined) => {
                if (!thumbprint) {
                  resolve(null);
                  return;
                }
                this.webPkiService.lerCertificado(thumbprint).subscribe({
                  next: (certContent) => resolve({ thumbprint, certContent }),
                  error: (err) => reject(err),
                });
              });
            },
            error: (err) => reject(err),
          });
        },
        error: (err) => reject(err),
      });
    });
  }
}
